import React, { useState } from "react";
import { Loader2, Search, MapPin, Ruler, Building2, AlertTriangle, FileText } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { toast } from "sonner";

// Fix default Leaflet icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const Stat = ({ label, value, hint }) => (
  <div className="bg-white border border-zinc-200 rounded-sm p-4">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{label}</div>
    <div className="mt-1.5 text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>{value}</div>
    {hint && <div className="text-xs text-zinc-500 mt-0.5">{hint}</div>}
  </div>
);

export default function PropertyAnalysis() {
  const [address, setAddress] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e?.preventDefault?.();
    if (address.trim().length < 3) { toast.warning("Skriv en längre adress"); return; }
    setLoading(true);
    try {
      const { data: r } = await api.get("/property", { params: { address } });
      setData(r);
    } catch (err) {
      toast.error("Kunde inte hämta fastighetsdata");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-blue-700 text-white flex items-center justify-center rounded-sm">
          <MapPin className="w-6 h-6 stroke-[1.5]" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Bygglovsguide</div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>Fastighetsanalys</h1>
        </div>
      </div>
      <p className="mt-3 text-zinc-600 max-w-2xl">Sök på adress för att se detaljplan, byggrätter, prickmark, höjder och övriga förutsättningar.</p>

      <form onSubmit={search} className="mt-8 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            data-testid="property-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="t.ex. Storgatan 12, Stockholm"
            className="w-full pl-10 pr-3 py-3 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-base bg-white"
          />
        </div>
        <button type="submit" data-testid="property-search" disabled={loading}
          className="bg-blue-700 hover:bg-blue-800 text-white px-6 rounded-sm font-medium text-sm flex items-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sök"}
        </button>
      </form>

      {data && data.ok && (
        <div className="mt-8 space-y-6" data-testid="property-result">
          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Adress</div>
            <h2 className="mt-1 text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>{data.address_normalized}</h2>
            <p className="text-sm text-zinc-600">
              {data.municipality && <>{data.municipality} kommun</>}
              {data.postcode && <> · {data.postcode}</>}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Markanvändning" value={data.primary_landuse || "Okänt"}
              hint={`${data.all_landuse?.length || 0} klasser i området`} />
            <Stat label="Närbyggnader" value={data.nearby_buildings_total}
              hint="Inom 60 m" />
            <Stat label="Plan-status"
              value={data.plan_status?.confidence ? `${Math.round(data.plan_status.confidence * 100)}%` : "—"}
              hint="Konfidens" />
            <Stat label="Byggrätt (uppskattn.)"
              value={data.byggratt_estimate?.estimated_max_bya_sqm ? `${data.byggratt_estimate.estimated_max_bya_sqm} kvm` : "—"}
              hint="BYA – verifiera mot detaljplan" />
          </div>

          <div className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-sm overflow-hidden">
              <div className="h-[400px]" data-testid="property-map">
                <MapContainer center={[data.coordinates.lat, data.coordinates.lon]} zoom={17} style={{ height: "100%", width: "100%" }}>
                  <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[data.coordinates.lat, data.coordinates.lon]}>
                    <Popup>{data.address_normalized}</Popup>
                  </Marker>
                  <Circle center={[data.coordinates.lat, data.coordinates.lon]} radius={60} pathOptions={{ color: "#1d4ed8", fillOpacity: 0.1 }} />
                </MapContainer>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-3">
              <div className="bg-white border border-zinc-200 rounded-sm p-5">
                <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> Detaljplane-status
                </div>
                <div className="mt-2 text-base font-medium">{data.plan_status?.verdict}</div>
                <div className="text-xs text-zinc-500 mt-1">
                  Konfidens: {Math.round((data.plan_status?.confidence || 0) * 100)}%
                </div>
              </div>
              {data.nearby_buildings?.length > 0 && (
                <div className="bg-white border border-zinc-200 rounded-sm p-5">
                  <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" /> Byggnader i närheten
                  </div>
                  <ul className="mt-2 text-sm space-y-1 text-zinc-700 max-h-32 overflow-y-auto">
                    {data.nearby_buildings.map((b, i) => (
                      <li key={i}>• {b.label}{b.levels && ` (${b.levels} våningar)`}</li>
                    ))}
                  </ul>
                </div>
              )}
              {data.byggratt_estimate?.note && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-900">
                  <div className="font-semibold mb-1 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Notering</div>
                  {data.byggratt_estimate.note}
                </div>
              )}
            </div>
          </div>

          {data.sources?.length > 0 && (
            <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-500 mb-2">Datakällor</div>
              <div className="flex flex-wrap gap-2">
                {data.sources.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline px-2 py-1 bg-white border border-zinc-200 rounded-sm">
                    {s.name} · {s.license}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="text-xs text-zinc-500">
            * Data hämtas från öppna geodata-källor (OpenStreetMap). För definitiva uppgifter om detaljplan, prickmark och byggrätt – kontakta kommunens bygglovsavdelning.
          </div>
        </div>
      )}

      {data && !data.ok && (
        <div className="mt-8 px-6 py-5 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-900">
          {data.error || "Adressen kunde inte hittas. Prova att skriva fullständig adress med kommun."}
        </div>
      )}
    </div>
  );
}
